import {
  Bus,
  CalendarCheck2,
  CalendarDays,
  CreditCard,
  FileText,
  Globe2,
  Hotel,
  Mail,
  MapPinned,
  MessageCircle,
  PanelTop,
  PlaneTakeoff,
  ReceiptText,
  UserPlus,
} from "lucide-react";
import { motion } from "motion/react";
import type { CSSProperties, ReactNode } from "react";
import { fadeUpItem, staggerContainer } from "./homepageMotion";
import { MotionSection } from "./HomepagePrimitives";

/**
 * Social proof strip beneath the hero.
 * @returns Social proof section
 */
export const HomepageSocialProofSection = () => {
  const logos = ["Northstar DMC", "Atlas Journeys", "Voyage Desk", "Summit Tours", "Blue Mile Travel"];

  return (
    <MotionSection id="social-proof" className="border-b border-gray-200 bg-[#F9FAFB] px-4 py-16 sm:px-6 lg:py-18">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-medium text-gray-600 sm:text-base">
            Trusted by forward-thinking DMCs, Tour Operators, and Travel Agencies.
          </p>
        </div>
        <motion.div
          className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={staggerContainer}
        >
          {logos.map((logo) => (
            <motion.div
              key={logo}
              variants={fadeUpItem}
              className="flex min-h-16 items-center justify-center rounded-xl border border-gray-200 bg-white px-5 text-center"
            >
              <p className="text-sm font-semibold tracking-[0.14em] text-[#131B2A]/38 grayscale">{logo}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </MotionSection>
  );
};

/**
 * Problem section showing operational pain points.
 * @returns Problem section
 */
export const HomepageProblemSection = () => {
  const workflowSteps = [
    {
      step: "Step 1",
      title: "Capture leads instantly",
      text: "Pull enquiries from Meta Ads, Google Ads, your website, WhatsApp, Gmail, and Outlook. TripboxCRM creates clean lead cards with source, owner, trip intent, and follow-up priority.",
      icon: <UserPlus className="h-6 w-6" />,
      tone: "bg-[#131B2A] text-white",
      activeLabel: "Lead created",
      signals: ["Website form", "Incoming email", "WhatsApp enquiry"],
    },
    {
      step: "Step 2",
      title: "Build itineraries faster",
      text: "Use reusable templates, pricing rules, supplier inventory, and branded proposal layouts so your team can quote in minutes instead of rebuilding every file from scratch.",
      icon: <MapPinned className="h-6 w-6" />,
      tone: "bg-white text-[#131B2A] ring-1 ring-gray-200",
      activeLabel: "Proposal ready",
      signals: ["Day plan", "Markup applied", "PDF generated"],
    },
    {
      step: "Step 3",
      title: "Confirm bookings",
      text: "Lock suppliers, generate confirmations, issue vouchers, and keep every booking status visible. No more searching chats to know whether a trip is actually ready.",
      icon: <CalendarCheck2 className="h-6 w-6" />,
      tone: "bg-[#131B2A] text-white",
      activeLabel: "Booking synced",
      signals: ["Supplier hold", "Voucher issued", "Departure active"],
    },
    {
      step: "Step 4",
      title: "Track payments",
      text: "Create payment schedules, send invoices, and monitor collections from the same workspace. Finance gets clear status instead of scattered screenshots and spreadsheet formulas.",
      icon: <CreditCard className="h-6 w-6" />,
      tone: "bg-(--brand-primary) text-[#131B2A]",
      activeLabel: "Collection tracked",
      signals: ["Deposit due", "Invoice sent", "Balance received"],
    },
  ];

  const workflowCard = {
    hidden: (index: number) => ({
      opacity: 0,
      y: 34,
      scale: 0.94,
      rotateX: index % 2 === 0 ? 6 : -6,
      filter: "blur(10px)",
    }),
    visible: (index: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      filter: "blur(0px)",
      transition: {
        type: "spring" as const,
        stiffness: 290,
        damping: 26,
        delay: index * 0.09,
      },
    }),
  };

  const renderWorkflowMock = (step: (typeof workflowSteps)[number], index: number) => {
    if (index === 0) {
      return (
        <div className="mt-7 rounded-xl border border-gray-200 bg-[#FBFBFB] p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2A]">
              <span className="tb-loop-pulse h-2.5 w-2.5 rounded-full bg-(--brand-primary)" />
              {step.activeLabel}
            </div>
            <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-[#131B2A]/65">
              Auto routed
            </span>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              { label: "Website", icon: <Globe2 className="h-4 w-4" />, count: "+12" },
              { label: "Email", icon: <Mail className="h-4 w-4" />, count: "+08" },
              { label: "WhatsApp", icon: <MessageCircle className="h-4 w-4" />, count: "+19" },
            ].map((source, sourceIndex) => (
              <div
                key={source.label}
                className="tb-ops-oscillate rounded-lg border border-gray-200 bg-white p-3"
                style={{ "--tb-loop-delay": `${sourceIndex * 0.18}s` } as CSSProperties}
              >
                <div
                  className="tb-loop-icon flex h-9 w-9 items-center justify-center rounded-lg bg-[#131B2A] text-white"
                  style={{ "--tb-loop-delay": `${sourceIndex * 0.16}s` } as CSSProperties}
                >
                  {source.icon}
                </div>
                <p className="mt-3 text-[11px] font-medium text-gray-500">{source.label}</p>
                <p className="text-lg font-semibold text-[#131B2A]">{source.count}</p>
              </div>
            ))}
          </div>

          <div className="tb-flow-line mx-3 mt-3 h-px bg-gray-200" />

          <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-500">New lead card</p>
                <p className="mt-1 truncate text-sm font-semibold text-[#131B2A]">Dubai family trip, 6 nights</p>
              </div>
              <span className="tb-ops-oscillate rounded-full border border-(--brand-primary)/45 bg-(--brand-primary)/10 px-2.5 py-1 text-[11px] font-semibold text-[#131B2A]">
                Hot lead
              </span>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              {[
                ["Owner", "Aarav"],
                ["Source", "Website"],
                ["Follow-up", "10:30 AM"],
              ].map(([label, value], fieldIndex) => (
                <div
                  key={label}
                  className="tb-node-float rounded-lg border border-gray-200 bg-[#FBFBFB] p-2"
                  style={{ "--tb-loop-delay": `${fieldIndex * 0.13}s` } as CSSProperties}
                >
                  <p className="text-[10px] font-medium text-gray-500">{label}</p>
                  <p className="mt-0.5 text-xs font-semibold text-[#131B2A]">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (index === 1) {
      return (
        <div className="mt-7 rounded-xl border border-gray-200 bg-[#FBFBFB] p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2A]">
              <span className="tb-loop-pulse h-2.5 w-2.5 rounded-full bg-(--brand-primary)" />
              {step.activeLabel}
            </div>
            <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-[#131B2A]/65">
              60 sec
            </span>
          </div>

          <div className="mt-4 grid gap-3">
            {[
              { day: "D1", title: "Arrival + hotel check-in", icon: <Hotel className="h-4 w-4" /> },
              { day: "D2", title: "City tour + guide assigned", icon: <Bus className="h-4 w-4" /> },
              { day: "D3", title: "Departure transfer", icon: <PlaneTakeoff className="h-4 w-4" /> },
            ].map((item, itemIndex) => (
              <div
                key={item.title}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 transition-all duration-200 group-hover:border-(--brand-primary)/55"
              >
                <div
                  className="tb-node-float flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#131B2A] text-xs font-semibold text-white"
                  style={{ "--tb-loop-delay": `${itemIndex * 0.16}s` } as CSSProperties}
                >
                  {item.day}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2A]">
                    <span className="text-(--brand-primary)">{item.icon}</span>
                    <span className="truncate">{item.title}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="h-1.5 w-16 rounded-full bg-gray-200" />
                    <span
                      className="tb-flow-line h-px flex-1 bg-gray-200"
                      style={{ "--tb-loop-delay": `${itemIndex * 0.2}s` } as CSSProperties}
                    />
                    <span className="h-1.5 w-10 rounded-full bg-gray-200" />
                  </div>
                </div>
                <span
                  className="tb-ops-oscillate rounded-md border border-gray-200 bg-[#FBFBFB] px-2 py-1 text-[11px] font-semibold text-[#131B2A]/60"
                  style={{ "--tb-loop-delay": `${itemIndex * 0.14}s` } as CSSProperties}
                >
                  Added
                </span>
              </div>
            ))}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-[11px] font-medium text-gray-500">Pricing logic</p>
              <p className="mt-1 text-lg font-semibold text-[#131B2A]">18% markup</p>
            </div>
            <div className="tb-ops-oscillate rounded-lg border border-(--brand-primary)/45 bg-white p-3">
              <p className="text-[11px] font-medium text-gray-500">Proposal</p>
              <div className="mt-1 flex items-center gap-2 text-lg font-semibold text-[#131B2A]">
                <FileText className="tb-loop-icon h-4 w-4 text-(--brand-primary)" />
                PDF ready
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (index === 2) {
      return (
        <div className="mt-7 rounded-xl border border-gray-200 bg-[#FBFBFB] p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2A]">
              <span className="tb-loop-pulse h-2.5 w-2.5 rounded-full bg-(--brand-primary)" />
              {step.activeLabel}
            </div>
            <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-[#131B2A]/65">
              Ops board
            </span>
          </div>

          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3 text-xs font-semibold text-[#131B2A]/60">
              <span>Service</span>
              <span>Status</span>
            </div>
            <div className="divide-y divide-gray-200">
              {[
                { label: "Hotel confirmation", status: "Locked", icon: <Hotel className="h-4 w-4" /> },
                { label: "Guide + transfer", status: "Assigned", icon: <Bus className="h-4 w-4" /> },
                { label: "Client voucher", status: "Issued", icon: <FileText className="h-4 w-4" /> },
              ].map((item, itemIndex) => (
                <div key={item.label} className="flex items-center justify-between gap-3 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div
                      className="tb-loop-icon flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-[#FBFBFB] text-[#131B2A]"
                      style={{ "--tb-loop-delay": `${itemIndex * 0.15}s` } as CSSProperties}
                    >
                      {item.icon}
                    </div>
                    <span className="truncate text-sm font-semibold text-[#131B2A]">{item.label}</span>
                  </div>
                  <span
                    className="tb-flow-line hidden h-px min-w-10 flex-1 bg-gray-200 sm:block"
                    style={{ "--tb-loop-delay": `${itemIndex * 0.18}s` } as CSSProperties}
                  />
                  <span
                    className="tb-ops-oscillate rounded-full border border-(--brand-primary)/45 bg-(--brand-primary)/10 px-2.5 py-1 text-[11px] font-semibold text-[#131B2A]"
                    style={{ "--tb-loop-delay": `${itemIndex * 0.14}s` } as CSSProperties}
                  >
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3">
            {["Supplier", "Voucher", "Departure"].map((label, itemIndex) => (
              <div
                key={label}
                className="tb-ops-oscillate rounded-lg border border-gray-200 bg-white p-3 text-center"
                style={{ "--tb-loop-delay": `${itemIndex * 0.16}s` } as CSSProperties}
              >
                <span
                  className="tb-loop-pulse mx-auto block h-2.5 w-2.5 rounded-full bg-(--brand-primary)"
                  style={{ "--tb-loop-delay": `${itemIndex * 0.18}s` } as CSSProperties}
                />
                <p className="mt-2 text-[11px] font-semibold text-[#131B2A]/65">{label}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (index === 3) {
      return (
        <div className="mt-7 rounded-xl border border-gray-200 bg-[#FBFBFB] p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2A]">
              <span className="tb-loop-pulse h-2.5 w-2.5 rounded-full bg-(--brand-primary)" />
              {step.activeLabel}
            </div>
            <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-[#131B2A]/65">
              Finance view
            </span>
          </div>

          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 pb-3">
              <div>
                <p className="text-[11px] font-medium text-gray-500">Booking value</p>
                <p className="mt-1 text-2xl font-semibold text-[#131B2A]">₹4.82L</p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-(--brand-primary) text-[#131B2A]">
                <ReceiptText className="tb-loop-icon h-5 w-5" />
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {[
                { label: "Deposit invoice", amount: "₹1.20L", status: "Paid" },
                { label: "Supplier advance", amount: "₹84K", status: "Due 18 Jul" },
                { label: "Final balance", amount: "₹2.78L", status: "Scheduled" },
              ].map((item, itemIndex) => (
                <div key={item.label} className="flex items-center justify-between gap-3 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className="tb-loop-pulse h-2.5 w-2.5 shrink-0 rounded-full bg-(--brand-primary)"
                      style={{ "--tb-loop-delay": `${itemIndex * 0.16}s` } as CSSProperties}
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[#131B2A]">{item.label}</p>
                      <p className="text-xs font-medium text-gray-500">{item.status}</p>
                    </div>
                  </div>
                  <span
                    className="tb-flow-line hidden h-px min-w-10 flex-1 bg-gray-200 sm:block"
                    style={{ "--tb-loop-delay": `${itemIndex * 0.18}s` } as CSSProperties}
                  />
                  <span
                    className="tb-ops-oscillate text-sm font-semibold text-[#131B2A]"
                    style={{ "--tb-loop-delay": `${itemIndex * 0.12}s` } as CSSProperties}
                  >
                    {item.amount}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="tb-ops-oscillate rounded-lg border border-(--brand-primary)/45 bg-white p-3">
              <p className="text-[11px] font-medium text-gray-500">Collection health</p>
              <p className="mt-1 text-lg font-semibold text-[#131B2A]">63%</p>
            </div>
            <div
              className="tb-ops-oscillate rounded-lg border border-gray-200 bg-white p-3"
              style={{ "--tb-loop-delay": "0.18s" } as CSSProperties}
            >
              <p className="text-[11px] font-medium text-gray-500">Next reminder</p>
              <p className="mt-1 text-lg font-semibold text-[#131B2A]">Today</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-7 rounded-xl border border-gray-200 bg-[#FBFBFB] p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2A]">
            <span className="tb-loop-pulse h-2.5 w-2.5 rounded-full bg-(--brand-primary)" />
            {step.activeLabel}
          </div>
          <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-[#131B2A]/65">
            Active
          </span>
        </div>

        <div className="mt-4 space-y-3">
          {step.signals.map((signal, signalIndex) => (
            <div key={signal} className="flex items-center gap-3">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-[11px] font-semibold text-[#131B2A]/70">
                {signalIndex + 1}
              </div>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                <div
                  className="tb-progress-breathe h-full rounded-full bg-[#131B2A]"
                  style={
                    {
                      width: `${88 - signalIndex * 14}%`,
                      "--tb-loop-delay": `${signalIndex * 0.18}s`,
                    } as CSSProperties
                  }
                />
              </div>
              <span className="hidden min-w-28 text-right text-xs font-medium text-gray-500 sm:block">{signal}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <MotionSection id="problem" className="overflow-hidden border-b border-gray-200 bg-white px-4 py-24 sm:px-6">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto mb-12 max-w-5xl text-center">
          <h2 className="text-balance text-4xl font-semibold tracking-normal text-[#131B2A] sm:text-5xl lg:text-[3.5rem] lg:leading-tight">
            Still running your travel business on spreadsheets and scattered WhatsApp messages?
          </h2>
          <motion.p
            className="mx-auto mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-gray-600"
            initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, amount: 0.7 }}
            transition={{ type: "spring", stiffness: 360, damping: 30, delay: 0.12 }}
          >
            TripboxCRM replaces scattered tabs, inboxes, and message threads with one guided workflow from first enquiry
            to final collection.
          </motion.p>
        </div>

        <motion.div
          className="relative mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <div className="tb-workflow-track-x left-[6%] right-[6%] top-1/2 z-0 hidden h-px lg:block" />
          <div
            className="tb-workflow-track-y bottom-[12%] left-1/2 top-[12%] z-0 hidden w-px lg:block"
            style={{ "--tb-loop-delay": "0.55s" } as CSSProperties}
          />

          {workflowSteps.map((step, index) => (
            <motion.article
              key={step.title}
              custom={index}
              variants={workflowCard}
              className="group relative z-10"
              whileHover={{ y: -5, scale: 1.01 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            >
              <div className="relative h-full overflow-hidden rounded-2xl border border-gray-200 bg-white p-7 shadow-sm transition-all duration-200 hover:border-(--brand-primary) hover:shadow-[0_8px_20px_rgba(19,27,42,0.08)] sm:p-8">
                <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-(--brand-primary) opacity-0 transition-opacity duration-200 group-hover:opacity-100" />

                <div className="flex items-start gap-5">
                  <div
                    className={`tb-loop-icon flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl ${step.tone}`}
                  >
                    {step.icon}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-(--brand-primary)">{step.step}</p>
                    <h3 className="mt-1 text-2xl font-semibold tracking-tight text-[#131B2A] sm:text-3xl">
                      {step.title}
                    </h3>
                  </div>
                </div>

                <p className="mt-6 max-w-[64ch] text-base leading-relaxed text-gray-600">{step.text}</p>

                {renderWorkflowMock(step, index)}
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </MotionSection>
  );
};

type IntegrationNode = {
  label: string;
  x: number;
  y: number;
  logoSrc?: string;
  logoIcon?: ReactNode;
};

const faviconPng = (domain: string) => `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

/**
 * Logo node for an integration source.
 * @param props Node props
 * @returns Positioned tool node
 */
const IntegrationToolNode = ({ node, index }: { node: IntegrationNode; index: number }) => (
  <motion.div
    className="absolute z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2"
    style={{ left: `${node.x}%`, top: `${node.y}%` }}
    initial={{ opacity: 0, scale: 0.95 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.5 }}
    whileHover={{ scale: 0.98 }}
    transition={{ type: "spring", stiffness: 400, damping: 30 }}
  >
    <div
      className="tb-node-float group flex min-w-28 flex-col overflow-hidden rounded-2xl border border-gray-200/90 bg-white/95 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_14px_34px_rgba(15,23,42,0.07)] ring-1 ring-white transition duration-200 hover:-translate-y-0.5 hover:border-(--brand-primary)/80 hover:shadow-[0_1px_2px_rgba(15,23,42,0.06),0_18px_42px_rgba(15,23,42,0.1)] sm:min-w-36"
      style={{ "--tb-loop-delay": `${index * 0.14}s` } as CSSProperties}
    >
      <div className="relative flex h-20 items-center justify-center border-b border-gray-100 bg-[linear-gradient(180deg,#FFFFFF_0%,#F7F9FC_100%)] sm:h-24">
        <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
        {node.logoSrc ? (
          <img
            alt={`${node.label} logo`}
            src={node.logoSrc}
            className="h-12 w-12 object-contain sm:h-14 sm:w-14"
            loading="lazy"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-gray-200 bg-white text-[#131B2A] shadow-[0_1px_2px_rgba(15,23,42,0.06)] sm:h-14 sm:w-14">
            {node.logoIcon}
          </div>
        )}
      </div>
      <div className="px-3 py-2 text-center">
        <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-[#131B2A]">{node.label}</p>
      </div>
    </div>
  </motion.div>
);

/**
 * Dotted connector with animated data flow.
 * @param props Connector props
 * @returns SVG connector paths
 */
const IntegrationConnector = ({ node, index }: { node: IntegrationNode; index: number }) => {
  const dx = 50 - node.x;
  const dy = 50 - node.y;
  const distance = Math.hypot(dx, dy) || 1;
  const bend = index % 2 === 0 ? 4.2 : -4.2;
  const normalX = (-dy / distance) * bend;
  const normalY = (dx / distance) * bend;
  const c1x = node.x + dx * 0.36 + normalX;
  const c1y = node.y + dy * 0.36 + normalY;
  const c2x = 50 - dx * 0.32 + normalX;
  const c2y = 50 - dy * 0.32 + normalY;
  const path = `M ${node.x} ${node.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, 50 50`;

  return (
    <>
      <path
        d={path}
        fill="none"
        stroke="#94A3B8"
        strokeDasharray="1 4"
        strokeLinecap="round"
        strokeWidth="0.28"
        opacity="0.7"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={path}
        fill="none"
        stroke="#131B2A"
        strokeDasharray="0.8 7"
        strokeLinecap="round"
        strokeWidth="0.32"
        opacity="0.18"
        vectorEffect="non-scaling-stroke"
      />
      <circle r="0.32" fill="#131B2A">
        <animateMotion dur="2.7s" begin={`${index * 0.13}s`} repeatCount="indefinite" path={path} />
        <animate
          attributeName="opacity"
          values="0;0.84;0.84;0"
          dur="2.7s"
          begin={`${index * 0.13}s`}
          repeatCount="indefinite"
        />
      </circle>
      <circle r="0.2" fill="var(--brand-primary)">
        <animateMotion dur="2.1s" begin={`${0.42 + index * 0.11}s`} repeatCount="indefinite" path={path} />
        <animate
          attributeName="opacity"
          values="0;0.95;0.95;0"
          dur="2.1s"
          begin={`${0.42 + index * 0.11}s`}
          repeatCount="indefinite"
        />
      </circle>
    </>
  );
};

/**
 * Inbound integrations diagram.
 * @returns Integrations section
 */
export const HomepageIntegrationsSection = () => {
  const integrations: IntegrationNode[] = [
    { label: "Website", x: 50, y: 10, logoIcon: <PanelTop className="h-7 w-7 stroke-[1.8]" /> },
    { label: "Google Ads", x: 78, y: 19, logoSrc: faviconPng("ads.google.com") },
    { label: "WhatsApp", x: 89, y: 42, logoSrc: "/images/whatsapp.svg" },
    { label: "Gmail", x: 84, y: 65, logoSrc: "/images/gmail.svg" },
    { label: "Outlook", x: 65, y: 84, logoSrc: faviconPng("outlook.live.com") },
    { label: "Calendar", x: 35, y: 84, logoIcon: <CalendarDays className="h-7 w-7 stroke-[1.8]" /> },
    { label: "Zapier", x: 16, y: 65, logoSrc: faviconPng("zapier.com") },
    { label: "Slack", x: 11, y: 42, logoSrc: "/images/slack.svg" },
    { label: "Meta Ads", x: 22, y: 19, logoSrc: faviconPng("meta.com") },
  ];

  return (
    <MotionSection
      id="integrations"
      className="relative isolate overflow-hidden border-b border-gray-200 bg-[#F7F8FA] px-4 py-20 sm:px-6 lg:py-24"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.98)_0%,rgba(249,250,251,0.88)_42%,rgba(241,245,249,0.78)_100%)]" />
        <div className="tb-animated-grid absolute inset-0 opacity-[0.5] [background-image:linear-gradient(rgba(19,27,42,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(19,27,42,0.06)_1px,transparent_1px)] [background-size:56px_56px]" />
        <div className="absolute inset-0 opacity-[0.34] [background-image:radial-gradient(circle_at_1px_1px,rgba(19,27,42,0.13)_1px,transparent_0)] [background-size:18px_18px] [mask-image:radial-gradient(ellipse_at_center,black_0%,black_44%,transparent_78%)]" />
        <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-[#131B2A]/12 to-transparent" />
        <div className="absolute left-0 top-[55%] h-px w-full bg-gradient-to-r from-transparent via-[#131B2A]/10 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#F7F8FA] to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#F7F8FA] to-transparent" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-balance text-4xl font-semibold tracking-normal text-[#131B2A] sm:text-5xl lg:text-[3.2rem] lg:leading-tight">
            Plays nice with the tools you already use.
          </h2>
        </div>

        <div className="mx-auto mt-14 max-w-6xl">
          <div className="relative min-h-[42rem] overflow-hidden rounded-[2rem] border border-gray-200/80 bg-white/35 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_30px_80px_rgba(15,23,42,0.08)] ring-1 ring-white/80 sm:min-h-[46rem]">
            <div aria-hidden="true" className="absolute inset-4 rounded-[1.5rem] border border-white/80" />
            <div aria-hidden="true" className="absolute inset-8 rounded-[1.15rem] border border-gray-200/55" />
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.88)_0%,rgba(255,255,255,0.44)_38%,transparent_72%)]"
            />
            <div
              aria-hidden="true"
              className="tb-animated-grid absolute inset-0 opacity-[0.45] [background-image:linear-gradient(rgba(19,27,42,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(19,27,42,0.045)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,black_0%,black_58%,transparent_84%)]"
            />
            <div aria-hidden="true" className="absolute left-8 top-8 h-8 w-8 border-l border-t border-[#131B2A]/20" />
            <div aria-hidden="true" className="absolute right-8 top-8 h-8 w-8 border-r border-t border-[#131B2A]/20" />
            <div
              aria-hidden="true"
              className="absolute bottom-8 left-8 h-8 w-8 border-b border-l border-[#131B2A]/20"
            />
            <div
              aria-hidden="true"
              className="absolute bottom-8 right-8 h-8 w-8 border-b border-r border-[#131B2A]/20"
            />
            <svg
              aria-hidden="true"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="absolute inset-0 h-full w-full"
            >
              <defs>
                <radialGradient id="integration-core" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.18" />
                  <stop offset="54%" stopColor="#131B2A" stopOpacity="0.05" />
                  <stop offset="100%" stopColor="#131B2A" stopOpacity="0" />
                </radialGradient>
              </defs>
              <circle cx="50" cy="50" r="17" fill="url(#integration-core)" opacity="0.9" />
              <path
                d="M38 50H46M54 50H62M50 38V46M50 54V62"
                stroke="#131B2A"
                strokeOpacity="0.14"
                strokeWidth="0.35"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
              {integrations.map((node, index) => (
                <IntegrationConnector key={node.label} node={node} index={index} />
              ))}
            </svg>

            <div className="absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-3">
              <div className="tb-border-pulse tb-loop-shine relative flex h-32 w-52 items-center justify-center overflow-hidden rounded-[1.65rem] border border-(--brand-primary)/45 bg-[#131B2A] px-7 shadow-[0_1px_2px_rgba(15,23,42,0.16),0_24px_54px_rgba(15,23,42,0.24)] ring-1 ring-[#131B2A]/10 sm:h-40 sm:w-64">
                <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-(--brand-primary)/70 to-transparent" />
                <span className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.1)_0%,transparent_58%)]" />
                <img
                  alt="TripboxCRM"
                  src="/logo_text.png"
                  className="block h-auto max-h-14 w-full max-w-40 object-contain sm:max-h-16 sm:max-w-48"
                />
              </div>
              <span className="rounded-full border border-gray-200 bg-white px-4 py-1.5 text-xs font-semibold text-[#131B2A] shadow-[0_4px_12px_rgba(15,23,42,0.06)]">
                TripboxCRM
              </span>
            </div>

            {integrations.map((node, index) => (
              <IntegrationToolNode key={node.label} node={node} index={index} />
            ))}
          </div>
        </div>
      </div>
    </MotionSection>
  );
};
